import { GRADIENT_CONFIG, TOOLTIP_STYLES } from './constants';
import { FormatConfig, GroupData } from './types';
import { formatValue } from './formatUtils';

/**
 * Generate table rows for tooltip groups
 */
export const generateTooltipTableRows = (
  groups: GroupData[],
  totalValueAbs: number,
  originalTotalValue: number,
  formatConfig: FormatConfig,
  isFirstOrLast?: boolean
): string => {
  return groups
    .map((group: GroupData, idx: number) => {
      const groupValue = formatValue(group.Value, formatConfig);
      const groupValueAbs = Math.abs(group.Value || 0);
      const percentage =
        totalValueAbs > 0
          ? `${group.Value < 0 ? '-' : ''}${((groupValueAbs / totalValueAbs) * 100).toFixed(1)}`
          : '0.0';

      // Use default color for first/last columns, otherwise use positive/negative colors
      let baseColor: string;
      if (isFirstOrLast) {
        baseColor = GRADIENT_CONFIG.DEFAULT_COLOR;
      } else {
        baseColor =
          group.Value >= 0
            ? GRADIENT_CONFIG.POSITIVE_COLOR
            : GRADIENT_CONFIG.NEGATIVE_COLOR;
      }

      return `
      <tr style="border-bottom: 1px solid rgba(0,0,0,0.1);">
        <td style="padding: 4px 8px; vertical-align: middle;">
          <div style="display: flex; align-items: center;">
            <div style="width: 14px; height: 14px; background-color: ${baseColor}; margin-right: 8px; border: 1px solid rgba(255,255,255,0.5); border-radius: 2px;"></div>
            <span style="font-weight: 500;">${group.GroupName || `Group ${idx + 1}`}</span>
          </div>
        </td>
        <td style="padding: 4px 8px; text-align: right; font-family: monospace;">${groupValue}</td>
        <td style="padding: 4px 8px; text-align: right; color: #666; font-size: 0.9em;">${percentage}%</td>
      </tr>
    `;
    })
    .join('');
};

/**
 * Generate complete tooltip HTML structure
 */
export const generateTooltipHTML = (
  outputName: string,
  value: string,
  totalValue: number,
  formatConfig: FormatConfig,
  groups?: GroupData[],
  isFirstOrLast?: boolean
): string => {
  const baseTooltipStyles = `
    padding: ${TOOLTIP_STYLES.PADDING}; 
    background: white; 
    border-radius: ${TOOLTIP_STYLES.BORDER_RADIUS}; 
    box-shadow: ${TOOLTIP_STYLES.BOX_SHADOW};
  `;

  if (!groups || groups.length === 0) {
    return `
      <div style="${baseTooltipStyles} min-width: 200px;">
        <div style="font-size: 16px; font-weight: bold; color: ${TOOLTIP_STYLES.TITLE_COLOR}; margin-bottom: 8px;">
          ${outputName}
        </div>
        <div style="font-size: 14px; font-weight: 600; color: ${TOOLTIP_STYLES.TEXT_COLOR};">
          ${value}
        </div>
      </div>
    `;
  }

  const tableRows = generateTooltipTableRows(
    groups,
    Math.abs(totalValue),
    totalValue,
    formatConfig,
    isFirstOrLast
  );
  const tableHTML = `
    <table style="width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 0.9em;">
      <thead>
        <tr style="background-color: rgba(0,0,0,0.05); border-bottom: 2px solid rgba(0,0,0,0.1);">
          <th style="padding: 6px 8px; text-align: left; font-weight: 600; color: #333;">Group</th>
          <th style="padding: 6px 8px; text-align: right; font-weight: 600; color: #333;">Value</th>
          <th style="padding: 6px 8px; text-align: right; font-weight: 600; color: #333;">%</th>
        </tr>
      </thead>
      <tbody>
        ${tableRows}
      </tbody>
    </table>
  `;

  // Use default color for first/last columns, otherwise use positive/negative colors
  let valueColor: string;
  if (isFirstOrLast) {
    valueColor = GRADIENT_CONFIG.DEFAULT_COLOR;
  } else {
    valueColor =
      totalValue >= 0
        ? TOOLTIP_STYLES.POSITIVE_VALUE_COLOR
        : TOOLTIP_STYLES.NEGATIVE_VALUE_COLOR;
  }

  return `
    <div style="${baseTooltipStyles} min-width: ${TOOLTIP_STYLES.MIN_WIDTH}; max-width: ${TOOLTIP_STYLES.MAX_WIDTH};">
      <div style="font-size: 16px; font-weight: bold; color: ${TOOLTIP_STYLES.TITLE_COLOR}; margin-bottom: 8px; border-bottom: 2px solid ${TOOLTIP_STYLES.BORDER_COLOR}; padding-bottom: 4px;">
        ${outputName}
      </div>
      <div style="font-size: 14px; font-weight: 600; color: ${TOOLTIP_STYLES.TEXT_COLOR}; margin-bottom: 4px; display: flex; justify-content: space-between; align-items: center;">
        <span>Total Value:</span>
        <span style="font-family: monospace; color: ${valueColor};">${value}</span>
      </div>
      ${tableHTML}
    </div>
  `;
};
